import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Save, Undo, Trash2, ArrowRight, Circle, 
  Pencil, Type, Check, Loader2, Minus, Plus 
} from 'lucide-react';

interface ImageAnnotatorProps {
  imageUrl: string;
  onSave: (annotatedImageUrl: string) => void;
  onClose: () => void;
}

type Tool = 'pencil' | 'arrow' | 'circle' | 'text';

const ImageAnnotator: React.FC<ImageAnnotatorProps> = ({ imageUrl, onSave, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tool, setTool] = useState<Tool>('pencil');
  const [color, setColor] = useState('#ef4444'); // Standard technical red
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState<ImageData[]>([]);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load image onto canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageUrl;
    img.onload = () => {
      // Calculate dimensions to fit container while maintaining aspect ratio
      const container = containerRef.current;
      if (!container) return;

      const maxWidth = container.clientWidth - 40;
      const maxHeight = container.clientHeight - 100;
      
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Initialize history
      setHistory([ctx.getImageData(0, 0, width, height)]);
      setImageLoaded(true);
    };
  }, [imageUrl]);

  const getPos = (e: any) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
    return { x, y };
  };

  const startDraw = (e: any) => {
    setIsDrawing(true);
    const pos = getPos(e);
    setStartPos(pos);
    
    if (tool === 'text') {
      const text = prompt('Enter annotation text:');
      if (text) {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (ctx) {
          ctx.font = 'bold 24px Inter, sans-serif';
          ctx.fillStyle = color;
          ctx.fillText(text, pos.x, pos.y);
          saveToHistory();
        }
      }
      setIsDrawing(false);
    }
  };

  const draw = (e: any) => {
    if (!isDrawing || tool === 'text') return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    const currentPos = getPos(e);

    // Restore last state before drawing preview for shapes
    if (tool !== 'pencil' && history.length > 0) {
      ctx.putImageData(history[history.length - 1], 0, 0);
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (tool === 'pencil') {
      ctx.lineTo(currentPos.x, currentPos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(currentPos.x, currentPos.y);
    } else if (tool === 'arrow') {
      const headlen = 15;
      const angle = Math.atan2(currentPos.y - startPos.y, currentPos.x - startPos.x);
      ctx.beginPath();
      ctx.moveTo(startPos.x, startPos.y);
      ctx.lineTo(currentPos.x, currentPos.y);
      ctx.lineTo(currentPos.x - headlen * Math.cos(angle - Math.PI / 6), currentPos.y - headlen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(currentPos.x, currentPos.y);
      ctx.lineTo(currentPos.x - headlen * Math.cos(angle + Math.PI / 6), currentPos.y - headlen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    } else if (tool === 'circle') {
      const radius = Math.sqrt(Math.pow(currentPos.x - startPos.x, 2) + Math.pow(currentPos.y - startPos.y, 2));
      ctx.beginPath();
      ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
      ctx.stroke();
    }
  };

  const stopDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      saveToHistory();
    }
  };

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) {
      const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setHistory(prev => [...prev, newData]);
    }
  };

  const undo = () => {
    if (history.length <= 1) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) {
      const newHistory = history.slice(0, -1);
      setHistory(newHistory);
      ctx.putImageData(newHistory[newHistory.length - 1], 0, 0);
    }
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      onSave(dataUrl);
    } catch (err) {
      alert("Markup export failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-900/95 flex flex-col animate-in fade-in duration-300 backdrop-blur-md">
      <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-slate-900 shadow-2xl">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="p-2.5 hover:bg-white/10 rounded-xl text-white/50 transition-all">
            <X size={24} />
          </button>
          <div>
            <h3 className="text-white text-sm font-black uppercase tracking-widest leading-none">Markup Studio</h3>
            <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest mt-1">Technical Annotation active</p>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-white/5 p-1 rounded-2xl border border-white/10">
          {[
            { id: 'pencil', icon: Pencil, label: 'Pencil' },
            { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
            { id: 'circle', icon: Circle, label: 'Circle' },
            { id: 'text', icon: Type, label: 'Text' }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTool(t.id as Tool)}
              className={`p-3 rounded-xl transition-all flex items-center gap-2 ${tool === t.id ? 'bg-white text-slate-900 shadow-lg' : 'text-white/40 hover:text-white hover:bg-white/10'}`}
              title={t.label}
            >
              <t.icon size={18} />
              <span className={`text-[10px] font-black uppercase tracking-widest ${tool === t.id ? 'inline-block' : 'hidden'}`}>{t.label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {['#ef4444', '#eab308', '#3b82f6'].map(c => (
              <button 
                key={c}
                onClick={() => setColor(c)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${color === c ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-50'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="h-6 w-px bg-white/10 mx-2" />
          <button onClick={undo} disabled={history.length <= 1} className="p-2.5 bg-white/5 text-white rounded-xl hover:bg-white/10 disabled:opacity-20 transition-all">
            <Undo size={20} />
          </button>
          <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 shadow-lg shadow-emerald-500/20 flex items-center gap-2 transition-all active:scale-[0.98]">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Markup
          </button>
        </div>
      </header>

      <div ref={containerRef} className="flex-1 flex items-center justify-center p-10 overflow-hidden relative">
        {!imageLoaded && (
          <div className="flex flex-col items-center gap-4 text-white/20">
            <Loader2 size={48} className="animate-spin" />
            <p className="text-xs font-black uppercase tracking-widest">Hydrating Artifact...</p>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden relative cursor-crosshair">
          <canvas
            ref={canvasRef}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            className="block"
          />
        </div>
      </div>
      
      <footer className="p-6 text-center">
        <p className="text-white/20 text-[9px] font-black uppercase tracking-[0.4em]">Verified Forensic Markup Layer v1.0</p>
      </footer>
    </div>
  );
};

export default ImageAnnotator;